import type { ReactElement } from 'react'
import type { TFunction } from 'i18next'
import { ChevronDown, X } from 'lucide-react'
import {
  INFRASTRUCTURE_ROLE_LABELS,
  type InfrastructureRole,
  getRoleDisplayLabel,
} from '../../../../types/case-container'
import type { RolesInputTab } from './use-roles-input'

interface SelectedChipsProps {
  value: string[]
  onRemove: (role: string) => void
}

export function SelectedChips({ value, onRemove }: SelectedChipsProps): ReactElement | null {
  if (value.length === 0) return null
  return (
    <div className="flex flex-wrap gap-2">
      {value.map((role) => (
        <span
          key={role}
          className="inline-flex items-center gap-1 rounded-full bg-lime-100 px-2.5 py-1 text-xs font-medium text-lime-700 dark:bg-[#8a9bba]/10 dark:text-lime-300"
        >
          {getRoleDisplayLabel(role)}
          <button
            type="button"
            onClick={() => {
              onRemove(role)
            }}
            className="ml-0.5 text-[#4e5e82] hover:text-[#5c6e94] dark:hover:text-lime-200"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
    </div>
  )
}

interface DropdownToggleProps {
  isOpen: boolean
  selectedCount: number
  onToggle: () => void
  t: TFunction<'cases'>
}

export function DropdownToggle({
  isOpen,
  selectedCount,
  onToggle,
  t,
}: DropdownToggleProps): ReactElement {
  return (
    <button
      type="button"
      onClick={onToggle}
      className="flex w-full items-center justify-between rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-100 dark:hover:bg-slate-700"
    >
      <span className="text-slate-500 dark:text-slate-400">
        {selectedCount === 0
          ? t('infrastructure.roles.selectRoles')
          : t('infrastructure.roles.rolesSelected', { count: selectedCount })}
      </span>
      <ChevronDown
        className={`h-4 w-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`}
      />
    </button>
  )
}

interface TabsProps {
  activeTab: RolesInputTab
  onSelect: (tab: RolesInputTab) => void
  customCount: number
  t: TFunction<'cases'>
}

export function Tabs({ activeTab, onSelect, customCount, t }: TabsProps): ReactElement {
  const baseClass = 'flex-1 px-3 py-2 text-sm font-medium'
  const activeClass =
    'text-lime-600 dark:text-lime-400 border-b-2 border-lime-600 dark:border-lime-400'
  const inactiveClass =
    'text-slate-500 dark:text-slate-400 hover:text-slate-700 dark:hover:text-slate-300'
  return (
    <div className="flex border-b border-slate-200 dark:border-slate-700">
      <button
        type="button"
        onClick={() => {
          onSelect('predefined')
        }}
        className={`${baseClass} ${activeTab === 'predefined' ? activeClass : inactiveClass}`}
      >
        {t('infrastructure.roles.predefined')}
      </button>
      <button
        type="button"
        onClick={() => {
          onSelect('custom')
        }}
        className={`${baseClass} ${activeTab === 'custom' ? activeClass : inactiveClass}`}
      >
        {t('infrastructure.roles.custom')} {customCount > 0 && `(${customCount.toString()})`}
      </button>
    </div>
  )
}

interface RoleCheckboxProps {
  selected: boolean
  onToggle: () => void
  label: string
}

function RoleCheckbox({ selected, onToggle, label }: RoleCheckboxProps): ReactElement {
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded px-2 py-1.5 hover:bg-slate-100 dark:hover:bg-slate-700">
      <input
        type="checkbox"
        checked={selected}
        onChange={onToggle}
        className="rounded border-slate-300 text-lime-600 focus:ring-[#4e5e82] dark:border-slate-600"
      />
      <span className="text-sm text-slate-700 dark:text-slate-300">{label}</span>
    </label>
  )
}

interface PredefinedListProps {
  value: string[]
  onToggle: (role: string) => void
}

export function PredefinedList({ value, onToggle }: PredefinedListProps): ReactElement {
  return (
    <div className="space-y-1 p-2">
      {(Object.entries(INFRASTRUCTURE_ROLE_LABELS) as [InfrastructureRole, string][]).map(
        ([key, label]) => (
          <RoleCheckbox
            key={key}
            selected={value.includes(key)}
            onToggle={() => {
              onToggle(key)
            }}
            label={label}
          />
        )
      )}
    </div>
  )
}

interface CustomTabProps {
  value: string[]
  customRolesInUse: string[]
  customRole: string
  setCustomRole: (value: string) => void
  onToggle: (role: string) => void
  onAddCustom: () => void
  onCustomKeyDown: (event: React.KeyboardEvent) => void
  t: TFunction<'cases'>
}

export function CustomTab(props: CustomTabProps): ReactElement {
  const {
    value,
    customRolesInUse,
    customRole,
    setCustomRole,
    onToggle,
    onAddCustom,
    onCustomKeyDown,
    t,
  } = props
  return (
    <div className="p-2">
      {customRolesInUse.length > 0 ? (
        <div className="mb-3 space-y-1">
          <p className="mb-1 px-2 text-xs font-medium text-slate-500 dark:text-slate-400">
            {t('infrastructure.roles.customRolesInCase')}
          </p>
          {customRolesInUse.map((role) => (
            <RoleCheckbox
              key={role}
              selected={value.includes(role)}
              onToggle={() => {
                onToggle(role)
              }}
              label={getRoleDisplayLabel(role)}
            />
          ))}
        </div>
      ) : (
        <p className="px-2 py-2 text-sm text-slate-500 dark:text-slate-400">
          {t('infrastructure.roles.noCustomRolesYet')}
        </p>
      )}
      <div className="my-2 border-t border-slate-200 dark:border-slate-700" />
      <AddCustomRoleField
        customRole={customRole}
        setCustomRole={setCustomRole}
        onAdd={onAddCustom}
        onKeyDown={onCustomKeyDown}
        t={t}
      />
    </div>
  )
}

interface AddCustomRoleFieldProps {
  customRole: string
  setCustomRole: (value: string) => void
  onAdd: () => void
  onKeyDown: (event: React.KeyboardEvent) => void
  t: TFunction<'cases'>
}

function AddCustomRoleField({
  customRole,
  setCustomRole,
  onAdd,
  onKeyDown,
  t,
}: AddCustomRoleFieldProps): ReactElement {
  return (
    <div>
      <p className="mb-1 px-2 text-xs font-medium text-slate-500 dark:text-slate-400">
        {t('infrastructure.roles.addNewCustomRole')}
      </p>
      <div className="flex gap-2">
        <input
          type="text"
          value={customRole}
          onChange={(event) => {
            setCustomRole(event.target.value)
          }}
          onKeyDown={onKeyDown}
          placeholder={t('infrastructure.placeholders.endpointName')}
          className="flex-1 rounded-md border border-slate-300 bg-white px-2 py-1.5 text-sm text-slate-900 placeholder:text-slate-400 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
        />
        <button
          type="button"
          onClick={onAdd}
          disabled={!customRole.trim()}
          className="rounded-md bg-[#4e5e82] px-3 py-1.5 text-sm font-medium text-white hover:bg-[#5c6e94] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {t('actions.add')}
        </button>
      </div>
    </div>
  )
}
